'use client'

import { ChevronDown } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useContext, useState } from 'react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Switch } from '@/components/ui/switch'
import EntryConditionContent from '@/features/order/order-form/components/widget/collapsible/entry-condition'
import { useOrderFormCollapsibleControls } from '@/features/order/order-form/hooks/use-order-form-collapsible-controls'
import { OrderFormCollapsibleContext } from '@/features/order/order-form/lib/order-form-collapsible-context'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { cn } from '@/lib/utils'
import { formatFirstLetterToUpperCase } from '@/lib/utils/format-util'
import { useOrderFormCollapsibleConfig } from '@/service/ui-state.hook'
import type { OrderFormCollapsibleConfig } from '@/state/order'

// Grid-widget path: no OrderFormCollapsibleContext provider is mounted there, so this
// component owns the config hook itself (today's original single-owner behavior).
function CollapsibleAreaInOrderFormWithOwnHook({ cardId }: { cardId: string }) {
  const { config, updateConfig } = useOrderFormCollapsibleConfig(cardId)
  return <CollapsibleAreaInOrderFormBody config={config} updateConfig={updateConfig} />
}

export default function CollapsibleAreaInOrderForm() {
  const { cardId } = useUnifiedOrderFormContext()
  const ctx = useContext(OrderFormCollapsibleContext)

  // Popup sheet path: OrderFormSheetContent owns useOrderFormCollapsibleConfig once and
  // shares it via context — calling the hook again here would race two local states
  // writing back to the same atom. Grid-widget path: no provider, own the hook locally.
  if (ctx) {
    return <CollapsibleAreaInOrderFormBody config={ctx.config} updateConfig={ctx.updateConfig} />
  }
  return <CollapsibleAreaInOrderFormWithOwnHook cardId={cardId} />
}

// `inline` sits inside the order form (collapsed, framed top and bottom); `detail` is the
// sheet's expanded right column (open by default, since expanding the sheet is an explicit
// "show me more").
export function CollapsibleAreaInOrderFormBody({
  config,
  updateConfig,
  layout = 'inline',
}: {
  config: Required<OrderFormCollapsibleConfig>
  updateConfig: (patch: Partial<OrderFormCollapsibleConfig>) => void
  layout?: 'inline' | 'detail'
}) {
  const t = useTranslations('orderForm')
  const isDetail = layout === 'detail'
  const [entryOpen, setEntryOpen] = useState(isDetail)

  const {
    triggerActive,
    triggerHasData,
    triggerLabel,
    editEntryConfig,
    setTriggerEnabled,
    clearTrigger,
  } = useOrderFormCollapsibleControls(config, updateConfig)

  return (
    <Collapsible
      data-tutorial="exits"
      open={entryOpen}
      onOpenChange={setEntryOpen}
      className={cn('w-full px-4', !isDetail && 'border-foreground/10 border-y')}
    >
      {/* Once a trigger is set up, a plain on/off switch sits in the header (it used to be an
          8px dot whose "click to disable" only showed on hover — easy to miss, and invisible on
          touch). Off keeps the settings and just disarms; Clear (below) wipes them. */}
      <div className="focus-row -mx-4 flex items-center gap-3 px-4">
        <CollapsibleTrigger asChild>
          <button
            className={cn(
              'focus-row-target hover:text-foreground/90 flex min-w-0 flex-1 items-center gap-2 py-2.5 text-sm font-medium transition-colors',
              triggerHasData && !triggerActive ? 'text-foreground/30' : 'text-foreground/60'
            )}
          >
            <span>{t('orderTrigger')}</span>
            {triggerHasData && triggerActive && triggerLabel && (
              <span className="text-foreground bg-foreground/10 truncate px-1.5 py-0.5 text-[10px] font-medium tracking-tight">
                {formatFirstLetterToUpperCase(triggerLabel)}
              </span>
            )}
          </button>
        </CollapsibleTrigger>
        {triggerHasData && (
          <Switch
            checked={triggerActive}
            onCheckedChange={setTriggerEnabled}
            aria-label={triggerActive ? t('disableOrderTrigger') : t('enableOrderTrigger')}
            title={triggerActive ? t('triggerArmedTitle') : t('triggerDisabledTitle')}
          />
        )}
        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-label={t('orderTrigger')}
            className="text-foreground/60 hover:text-foreground/90 -mr-1 flex size-6 shrink-0 cursor-pointer items-center justify-center transition-colors"
          >
            <ChevronDown
              className={cn('size-4 transition-transform duration-200', entryOpen && 'rotate-180')}
            />
          </button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="mx-auto w-full pb-3">
        <EntryConditionContent config={config} updateConfig={editEntryConfig} />
        {triggerHasData && (
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={clearTrigger}
              className="text-foreground/40 hover:text-foreground/80 text-xs transition-colors"
            >
              {t('clearTrigger')}
            </button>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
